<template>
    <!--
        NotchedBar Component

        A reusable container component that creates a distinctive notched corner design
        commonly used for CTA bars and premium content sections. The component uses
        CSS clip-path to create the notched effect with a consistent border ratio.

        The design consists of:
        - Outer container with larger notched corners (or flat top if flatTop prop is set)
        - Inner container with smaller notched corners (or flat top if flatTop prop is set)
        - Optional flat top edge via flatTop prop (defaults to notches on all sides)
        - White background border effect created by the difference between outer/inner
        - Configurable height, edge sizes, and layout modes
    -->
    <div class="nf-outer" :style="styleVars">
        <div class="nf-inner">
            <div class="nf-content" :class="contentClass">
                <div class="nf-left" :class="leftClass">
                    <slot name="left" />
                </div>
                <div class="nf-center" :class="centerClass">
                    <slot name="center" />
                </div>
                <div class="nf-right" :class="rightClass">
                    <slot name="right" />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { NAV_BAR_HEIGHT_REM } from '@/constants/layout'

/**
 * NotchedBar Component Props
 *
 * This component is designed as a reusable container that provides:
 * - Consistent notched corner aesthetic across the application
 * - Flexible height configuration with automatic border ratio maintenance
 * - Multiple layout modes for different use cases
 * - Default values that work well for most scenarios
 * - Override capabilities for custom implementations
 */
interface Props {
    height?: number // Total outer height in rem - the main dimension control
    outerEdgeSize?: number // Size of outer notched corners in rem
    edgeSize?: number // Size of inner notched corners in rem
    borderWidth?: number // Width of the white border effect in rem (used in CSS clip-path)
    backgroundVar?: string // CSS variable name for background color
    margin?: number // Outer margin spacing in rem
    padding?: number // Inner padding spacing in rem
    layout?: 'edge-pinned' | 'centered' | 'flex' | 'flex-end' // Content positioning strategy
    flatTop?: boolean // If true, top edge is flat (no notches). Defaults to false (notches on all sides)
    ui?: {
        left?: string
        center?: string
        right?: string
    }
}

// Default values optimized for common use cases
// These provide a good starting point while allowing full customization
const props = withDefaults(defineProps<Props>(), {
    height: NAV_BAR_HEIGHT_REM, // ~73px - balanced height for most CTA bars
    outerEdgeSize: 0.5, // 0.5rem outer notches
    edgeSize: 0.375, // 0.375rem inner notches
    borderWidth: 0.0625, // 0.0625rem border width
    backgroundVar: 'var(--ui-bg-primary)', // Dark background for contrast
    margin: 0.6, // 0.6rem margin for spacing
    padding: 0.25, // 0.25rem padding for content breathing room
    layout: 'edge-pinned', // Default layout with content pinned to edges
    flatTop: false, // Default to notches on all sides
    ui: () => ({
        left: '',
        center: '',
        right: '',
    }),
})

/**
 * Automatic Inner Height Calculation
 *
 * Instead of requiring manual inner height specification, this computed property
 * automatically calculates the inner height to maintain a consistent border ratio.
 * This ensures the white border effect remains visually consistent regardless
 * of the total height specified.
 *
 * Border ratio: 0.5rem total border width (0.25rem on each side)
 * This creates a subtle but visible border that enhances the premium feel
 */
const innerHeight = computed(() => {
    // Height difference: outer height - inner height creates the border effect
    // Using 0.5rem (8px) total height difference for consistent border ratio
    const heightDifference = 0.5
    return Math.max(props.height - heightDifference, 1) // Ensure minimum height of 1rem
})

/**
 * Clip-path Polygon Generators
 *
 * Generates the appropriate clip-path polygon based on whether flatTop is enabled.
 * When flatTop is false (default), all sides have notches.
 * When flatTop is true, only left, bottom, and right sides have notches (top is flat).
 */
const outerClipPath = computed(() => {
    const edgeSize = 'var(--nf-outer-edge)'
    const borderWidth = 'var(--nf-border)'

    // If flatTop is true, the top edge is flat (0% 0% to 100% 0%), otherwise it has notches on all sides
    return `polygon(
            ${props.flatTop ? '0%' : edgeSize} 0%,
            ${props.flatTop ? '100%' : `calc(100% - ${edgeSize})`} 0%,
            100% ${edgeSize},
            100% calc(100% - ${edgeSize}${props.flatTop ? ` - (${borderWidth} * 3)` : ''}),
            calc(100% - ${edgeSize}) ${props.flatTop ? `calc(100% - (${borderWidth} * 3))` : '100%'},
            ${edgeSize} ${props.flatTop ? `calc(100% - (${borderWidth} * 3))` : '100%'},
            0% calc(100% - ${edgeSize}${props.flatTop ? ` - (${borderWidth} * 3)` : ''}),
            0% ${edgeSize}
        )`
})

const innerClipPath = computed(() => {
    const edgeSize = 'var(--nf-edge)'

    return `polygon(
        ${props.flatTop ? `0%` : edgeSize} 0%,
        ${props.flatTop ? '100%' : `calc(100% - ${edgeSize})`} 0%,
        100% ${edgeSize},
        100% calc(100% - ${edgeSize}),
        calc(100% - ${edgeSize}) 100%,
        ${edgeSize} 100%,
        0% calc(100% - ${edgeSize}),
        0% ${edgeSize}
    )`
})

const innerAfterClipPath = computed(() => {
    const edgeSize = 'var(--nf-edge)'
    const borderWidth = 'var(--nf-border)'

    return `polygon(
        ${props.flatTop ? `calc(${borderWidth}) 0%` : `calc(${edgeSize} + ${borderWidth}) calc(${borderWidth})`},
        ${props.flatTop ? `calc(100% - ${borderWidth}) 0%` : `calc(100% - calc(${edgeSize} + ${borderWidth})) calc(${borderWidth})`},
        calc(100% - ${borderWidth}) calc(${edgeSize} + ${borderWidth}),
        calc(100% - ${borderWidth}) calc(100% - calc(${edgeSize} + ${borderWidth})),
        calc(100% - calc(${edgeSize} + ${borderWidth})) calc(100% - ${borderWidth}),
        calc(${edgeSize} + ${borderWidth}) calc(100% - ${borderWidth}),
        calc(${borderWidth}) calc(100% - calc(${edgeSize} + ${borderWidth})),
        calc(${borderWidth}) calc(${edgeSize} + ${borderWidth})
    )`
})

/**
 * CSS Custom Properties for Dynamic Styling
 *
 * These computed style variables allow the component to dynamically adjust
 * its appearance based on the props passed by the parent. This enables:
 * - Responsive height adjustments
 * - Consistent spacing using design tokens
 * - Theme-aware color application
 * - Flexible edge sizing for different contexts
 * - Conditional clip-path based on flatTop prop
 */
const styleVars = computed(
    () =>
        ({
            '--nf-height': `${props.height}rem`,
            '--nf-inner-height': `${innerHeight.value}rem`,
            '--nf-outer-edge': `${props.outerEdgeSize}rem`,
            '--nf-edge': `${props.edgeSize}rem`,
            '--nf-border': `${props.borderWidth}rem`,
            '--nf-bg': props.backgroundVar,
            '--nf-margin': `${props.margin}rem`,
            '--nf-padding': `${props.padding}rem`,
            '--nf-outer-clip-path': outerClipPath.value,
            '--nf-inner-clip-path': innerClipPath.value,
            '--nf-inner-after-clip-path': innerAfterClipPath.value,
        }) as Record<string, string>
)

/**
 * Layout Mode Classes
 *
 * The component supports multiple layout strategies to accommodate different
 * content arrangements and design requirements:
 *
 * - edge-pinned: Content positioned at left/right edges (default)
 * - centered: Content centered horizontally with equal spacing
 * - flex: Natural flexbox spacing for CTA bars and action items
 * - flex-end: Right-aligned content for secondary actions
 */
const contentClass = computed(() => ({
    'nf-content-centered': props.layout === 'centered',
    'nf-content-edge-pinned': props.layout === 'edge-pinned',
    'nf-content-flex': props.layout === 'flex',
    'nf-content-flex-end': props.layout === 'flex-end',
}))

const leftClass = computed(() => [
    props.ui.left,
    {
        'nf-left-centered': props.layout === 'centered',
        'nf-left-edge-pinned': props.layout === 'edge-pinned',
        'nf-left-flex': props.layout === 'flex',
        'nf-left-flex-end': props.layout === 'flex-end',
    },
])

const centerClass = computed(() => [
    props.ui.center,
    {
        'nf-center-centered': props.layout === 'centered',
        'nf-center-edge-pinned': props.layout === 'edge-pinned',
        'nf-center-flex': props.layout === 'flex',
        'nf-center-flex-end': props.layout === 'flex-end',
    },
])

const rightClass = computed(() => [
    props.ui.right,
    {
        'nf-right-centered': props.layout === 'centered',
        'nf-right-edge-pinned': props.layout === 'edge-pinned',
        'nf-right-flex': props.layout === 'flex',
        'nf-right-flex-end': props.layout === 'flex-end',
    },
])
</script>

<style scoped>
/**
 * NotchedBar Component Styles
 *
 * The styling uses CSS clip-path to create the distinctive notched corner effect.
 * Two layers are used:
 * 1. Outer container with larger notches (or flat top if flatTop prop is set)
 * 2. Inner container with smaller notches (or flat top if flatTop prop is set)
 *
 * When flatTop is false (default), all sides have notched corners.
 * When flatTop is true, the top edge is flat with notches on left, bottom, and right sides.
 * The white border effect is achieved by the difference between these two layers,
 * creating a sophisticated, premium appearance that's consistent with the brand.
 *
 * Pseudo elements are used to create the border effect, ensuring that the clip-path
 * doesn't prevent content from being visible outside of the navigation bar.
 */

/* Outer container - creates the boundaries for the inner container */
.nf-outer {
    height: var(--nf-height);
    margin: var(--nf-margin);
    padding: var(--nf-padding);
    position: relative;
    --edge-size: var(--nf-outer-edge);
}

/* Outer background layer - creates the main shape and background */
.nf-outer::before {
    content: '';
    position: absolute;
    inset: 0;
    background: var(--nf-bg);
    z-index: -2;
    clip-path: var(--nf-outer-clip-path);
}

/* Inner container - creates the border effect and content area */
.nf-inner {
    --edge-size: var(--nf-edge);
    --border-width: var(--nf-border);
    background: var(--nf-bg);
    border: 0;
    position: relative;
    isolation: isolate;
    width: 100%;
    height: var(--nf-inner-height);
}

/* White background layer - creates the border effect */
.nf-inner::before {
    content: '';
    position: absolute;
    inset: 0;
    background: var(--ui-bg-white);
    z-index: -2;
    clip-path: var(--nf-inner-clip-path);
}

/* Colored background layer - creates the main content background */
.nf-inner::after {
    content: '';
    position: absolute;
    inset: 0;
    background: var(--nf-bg);
    z-index: -1;
    clip-path: var(--nf-inner-after-clip-path);
}

/* Absolute position content wrapper to pad content without accidentally expanding width */
.nf-content {
    position: absolute;
    left: 0.25rem;
    right: 0.25rem;
    top: 0;
    height: 100%;
}

/* Layout Mode Styles */

/* Edge-pinned layout - content positioned at left/right edges */
.nf-left-edge-pinned,
.nf-right-edge-pinned {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
}

.nf-left-edge-pinned {
    left: var(--spacing-2);
}

.nf-right-edge-pinned {
    right: var(--spacing-2);
}

.nf-center-edge-pinned {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    align-items: center;
    justify-content: center;
}

/* Shared flex content styling (centered, flex, flex-end) */
.nf-content-centered,
.nf-content-flex,
.nf-content-flex-end {
    display: flex;
    align-items: center;
    justify-content: center;
}

/* Centered layout - content centered horizontally with equal spacing */
.nf-content-centered {
    gap: var(--spacing-4);
}

.nf-left-centered,
.nf-center-centered,
.nf-right-centered {
    display: flex;
    align-items: center;
}

/* Flex layout - natural spacing for CTA bars and action items */
.nf-content-flex {
    gap: var(--spacing-0-5);
}

.nf-left-flex,
.nf-center-flex,
.nf-right-flex {
    display: flex;
    align-items: center;
}

/* Flex-end layout - right-aligned content for secondary actions */
.nf-content-flex-end {
    gap: var(--spacing-1);
    padding: 0 var(--spacing-3);
}

.nf-left-flex-end,
.nf-center-flex-end,
.nf-right-flex-end {
    display: flex;
    align-items: center;
}
</style>
